import { Injectable } from '@angular/core';
import { ProjectRestService } from '@app/services/rest/project-rest.service';
import { UserRestService } from '@app/services/rest/user-rest.service';
import * as Reducers from '@app/store/reducers';
import { select, Store } from '@ngrx/store';
import { flatten } from 'lodash';
import { combineLatest, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, switchMapTo } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

type TBoolChecker = (rPerm: string[], gPerm: string[]) => boolean;
class BoolOperations {
	static AND = (reqPermissions: string[], givenPermissions: string[]): boolean => reqPermissions.every(req => givenPermissions.includes(req));
	static OR = (reqPermissions: string[], givenPermissions: string[]): boolean => reqPermissions.some(req => givenPermissions.includes(req));
}

@Injectable({
	providedIn: 'root'
})
export class PermissionService {

	public readonly permissions$: Observable<string[]>;
	public readonly globalPermissions$: Observable<string[]>;
	public readonly projectPermissions$: Observable<string[]>;

	constructor(
		private store: Store<never>,
		private userRestService: UserRestService,
		private projectRestService: ProjectRestService,
	) {

		const user$ = this.store.pipe(
			select(Reducers.getUser),
		);

		const project$ = this.store.pipe(
			select(Reducers.getActiveProject),
		);

		this.globalPermissions$ = user$.pipe(
			switchMap(user_state => {
				if (user_state?.authorized && user_state?.username) {
					return this.userRestService.userPermissions$(user_state.username).pipe(
						catchError((error: unknown) => {
							console.error(error);
							return of([]);
						}),
					);
				} else {
					return of([]);
				}
			}),
			shareReplay({ refCount: false, bufferSize: 1 }),
		);

		this.projectPermissions$ = combineLatest([user$, project$]).pipe(
			switchMap(([user_state, project]) => {
				if (user_state?.authorized && user_state?.username && project?.project_id) {
					return this.projectRestService.getProjectUserPermissions$(project.project_id, user_state.username).pipe(
						catchError((error: unknown) => {
							console.error(error);
							return of([]);
						}),
					);
				} else {
					return of([]);
				}
			}),
			shareReplay({ refCount: false, bufferSize: 1 }),
		);

		this.permissions$ = combineLatest([this.globalPermissions$, this.projectPermissions$]).pipe(
			map(([userPermissions, projectPermissions]) => flatten([userPermissions, projectPermissions])),
			catchError((error: unknown) => {
				console.error(error);
				return of([]);
			}),
			shareReplay({ refCount: false, bufferSize: 1 }),
		);
	}
	
	public getPermissionsByProjectId(projectId: string): Observable<string[]> {
		const user$ = this.store.pipe(
			select(Reducers.getUser),
		);
		const projectPermissions$ = user$.pipe(
			switchMap(user_state => {
				if (user_state?.authorized && user_state?.username && projectId) {
					return this.projectRestService.getProjectUserPermissions$(projectId, user_state.username).pipe(
						catchError((error: unknown) => {
							console.error(error);
							return of([]);
						}),
					);
				} else {
					return of([]);
				}
			}),
		);
		
		return combineLatest([this.globalPermissions$, projectPermissions$]).pipe(
			map(([userPermissions, projectPermissions]) => flatten([userPermissions, projectPermissions])),
			catchError((error: unknown) => {
				console.error(error);
				return of([]);
			}),
			shareReplay({ refCount: false, bufferSize: 1 }),
		);
	}


	/**
	 * Create Permission-Checker Predicate
	 *
	 * @param reqPermissions required permissions
	 * @param operationFn optional boolean operation, default: AND
	 * @returns Permission-Checker
	 */
	private permissionChecker(reqPermissions: string[], operationFn: TBoolChecker): (permissions: string[]) => boolean {
		return permissions => {
			if (operationFn(reqPermissions, permissions)) {
				return true;
			}
			if (!environment.production) {
				console.warn(`missing permission ${reqPermissions.join(', ')}`);
			}
			return false;
		};
	}

	/**
	 * Create RxJS filter to check permission(s)
	 * @param permission string[] of required Permission
	 * @param permissions$ Permissions observable
	 * @returns filter
	 */
	private checkPermissionsFilter<T>(permission: string[], permissions$: Observable<string[]>, operationFn: TBoolChecker): OperatorFunction<T, T> {
		return switchMap((state: T) => permissions$.pipe(
			filter(this.permissionChecker(permission, operationFn)),
			switchMapTo(of(state)),
		));
	}

	/**
	 * Check for permission
	 * @param permissions$ observable mit permissions
	 * @param permission permission
	 * @returns Filter that checks for the permission
	 */
	public checkPermissionInObs<T>(permissions$ = this.permissions$, permission: string): OperatorFunction<T, T> {
		return this.checkPermissionsFilter([permission], permissions$, BoolOperations.AND);
	}

	/**
	 * Check for permission(s)
	 * @param permissions$ observable mit permissions
	 * @param permissions permissions
	 * @returns Filter that checks for the permission
	 */
	public checkAllPermissionsInObs<T>(permissions$ = this.permissions$, ...permissions: string[]): OperatorFunction<T, T> {
		return this.checkPermissionsFilter(permissions, permissions$, BoolOperations.AND);
	}

	/**
	 * Check for permission(s)
	 * @param permissions$ observable mit permissions
	 * @param permissions permissions
	 * @returns Filter that checks for the permission
	 */
	public checkAnyPermissionsInObs<T>(permissions$ = this.permissions$, ...permissions: string[]): OperatorFunction<T, T> {
		return this.checkPermissionsFilter(permissions, permissions$, BoolOperations.OR);
	}
	
	/**
	 * Check for permission
	 * @param permission permission
	 * @returns Filter that checks for the permission
	 */
	public checkPermission<T>(permission: string): OperatorFunction<T, T> {
		return this.checkPermissionsFilter([permission], this.permissions$, BoolOperations.AND);
	}

	/**
	 * Check for permission(s)
	 * @param permissions permissions
	 * @returns Filter that checks for the permission
	 */
	public checkAllPermissions<T>(...permissions: string[]): OperatorFunction<T, T> {
		return this.checkPermissionsFilter(permissions, this.permissions$, BoolOperations.AND);
	}

	/**
	 * Check for global permission(s)
	 * @param permissions permissions
	 * @returns Filter that checks for the permission
	 */
	public checkAllGlobalPermissions<T>(...permissions: string[]): OperatorFunction<T, T> {
		return this.checkPermissionsFilter(permissions, this.globalPermissions$, BoolOperations.AND);
	}

	/**
	 * Check for project permission(s)
	 * @param permissions permissions
	 * @returns Filter that checks for the permission
	 */
	public checkAllProjectPermissions<T>(...permissions: string[]): OperatorFunction<T, T> {
		return this.checkPermissionsFilter(permissions, this.projectPermissions$, BoolOperations.AND);
	}

	/**
	 * Test against specific observable if user has permission
	 * @param permissions$ observable mit permissions
	 * @param permission permission
	 * @returns Observable<true | false>
	 */
	public hasPermissionInObs$(permissions$ = this.permissions$, permission: string): Observable<boolean> {
		return permissions$.pipe(
			map(this.permissionChecker([permission], BoolOperations.AND)),
		);
	}
	
	/**
	 * Test against specific observable if user has permission(s)
	 * @param permissions$ observable mit permissions
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAllPermissionsInObs$(permissions$ = this.permissions$, ...permissions: string[]): Observable<boolean> {
		return permissions$.pipe(
			map(this.permissionChecker(permissions, BoolOperations.AND)),
		);
	}

	/**
	 * Test against specific observable if user has any of the required permission(s)
	 * @param permissions$ observable mit permissions
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAnyPermissionsInObs$(permissions$ = this.permissions$, ...permissions: string[]): Observable<boolean> {
		return permissions$.pipe(
			map(this.permissionChecker(permissions, BoolOperations.OR)),
		);
	}

	/**
	 * Test if user has permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAllPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAllPermissionsInObs$(this.permissions$, ...permissions);
	}

	/**
	 * Test if user has global permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAllGlobalPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAllPermissionsInObs$(this.globalPermissions$, ...permissions);
	}

	/**
	 * Test if user has project permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAllProjectPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAllPermissionsInObs$(this.projectPermissions$, ...permissions);
	}

	/**
	 * Test if user has any of the required permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAnyPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.permissions$, ...permissions);
	}

	/**
	 * Test if user has any of the required permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAnyGlobalPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.globalPermissions$, ...permissions);
	}

	/**
	 * Test if user has any of the required permission(s)
	 * @param permissions permissions
	 * @returns Observable<true | false>
	 */
	public hasAnyProjectPermissions$(...permissions: string[]): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.projectPermissions$, ...permissions);
	}

	/**
	 * Test if user has the required permission
	 * @param permission permission
	 * @returns Observable<true | false>
	 */
	public hasPermission$(permission: string): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.permissions$, permission);
	}

	/**
	 * Test if user has the required permission
	 * @param permission permission
	 * @returns Observable<true | false>
	 */
	public hasGlobalPermission$(permission: string): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.globalPermissions$, permission);
	}

	/**
	 * Test if user has the required permission
	 * @param permission permission
	 * @returns Observable<true | false>
	 */
	public hasProjectPermission$(permission: string): Observable<boolean> {
		return this.hasAnyPermissionsInObs$(this.projectPermissions$, permission);
	}

}
